import { useState, useEffect, useRef } from "react";
import { getDocument, GlobalWorkerOptions, AnnotationLayer } from "pdfjs-dist";
import { getFilledPdfBlobUrl } from "./PdfImporter";
import "./pdfAnnotationLayer.css";

// Served by us, not fetched from a CDN. scripts/copy-pdf-worker.js copies the
// worker out of the installed pdfjs-dist on postinstall and prebuild, so the
// library and its worker can never end up on different versions, and PDF
// rendering does not depend on a third-party host being reachable.
GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const RENDER_WIDTH = 850;

async function loadPdfPages(rawBytes) {
  const pdf = await getDocument({ data: new Uint8Array(rawBytes) }).promise;
  const pages = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const baseVp = page.getViewport({ scale: 1 });
    const scale = RENDER_WIDTH / baseVp.width;
    const vp = page.getViewport({ scale });
    pages.push({ page, vp, scale, width: Math.round(vp.width), height: Math.round(vp.height) });
  }
  return { pdf, pages };
}

// ── Component ──────────────────────────────────────────────────────────────────
// readOnly=true  → Preview tab: filled PDF in iframe
// readOnly=false → Edit tab:    native pdfjs AnnotationLayer, pre-filled via annotationStorage
export default function PdfFormEditor({ report, template, rawBytes, onChange, readOnly = false }) {
  const [pdfData, setPdfData]         = useState(null);
  const [blobUrl, setBlobUrl]         = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const pdfDataCache = useRef(null);
  const prevBlobUrl  = useRef(null);

  // ── Preview: generate filled-PDF blob URL ─────────────────────────────────
  useEffect(() => {
    if (!rawBytes || !template || !readOnly) return;
    let alive = true;
    setLoadingView(true);
    getFilledPdfBlobUrl(rawBytes, report, template)
      .then(url => {
        if (!alive) { URL.revokeObjectURL(url); return; }
        if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
        prevBlobUrl.current = url;
        setBlobUrl(url);
      })
      .catch(console.error)
      .finally(() => { if (alive) setLoadingView(false); });
    return () => { alive = false; };
  }, [rawBytes, template, report, readOnly]); // eslint-disable-line

  // ── Edit: load PDF pages once, cache ─────────────────────────────────────
  useEffect(() => {
    if (!rawBytes || readOnly) return;
    if (pdfDataCache.current) { setPdfData(pdfDataCache.current); return; }
    setLoadingEdit(true);
    loadPdfPages(rawBytes)
      .then(data => { pdfDataCache.current = data; setPdfData(data); })
      .catch(err => { console.error("[PdfFormEditor]", err); alert("Could not render PDF: " + err.message); })
      .finally(() => setLoadingEdit(false));
  }, [rawBytes, readOnly]); // eslint-disable-line

  // ── Preview mode ───────────────────────────────────────────────────────────
  if (readOnly) {
    return (
      <div style={{ height: "calc(100vh - 130px)" }}>
        {!rawBytes || loadingView ? (
          <div style={S.placeholder}>{!rawBytes ? "Loading template…" : "Rendering preview…"}</div>
        ) : blobUrl ? (
          <iframe key={blobUrl} src={blobUrl} title="PDF Preview"
            style={{ width: "100%", height: "100%", border: "none", background: "#fff" }} />
        ) : (
          <div style={S.placeholder}>Could not render PDF preview.</div>
        )}
      </div>
    );
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 130px)" }}>
      <div style={{ flex: 1, overflowY: "auto", background: "#374151", padding: "20px 0" }}>
        {loadingEdit ? (
          <div style={S.placeholder}>Rendering PDF…</div>
        ) : pdfData ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
            {pdfData.pages.map((pg, pi) => (
              <PageWithAnnotations
                key={pi}
                pageData={pg}
                pdf={pdfData.pdf}
                report={report}
                onChange={onChange}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Single page: canvas + pdfjs AnnotationLayer pre-filled via annotationStorage ─
function PageWithAnnotations({ pageData, pdf, report, onChange }) {
  const containerRef = useRef(null);
  const annDivRef    = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;

    (async () => {
      const { page, vp, width, height } = pageData;

      // ── Render page to canvas ─────────────────────────────────────────────
      const canvas = document.createElement("canvas");
      canvas.width  = width;
      canvas.height = height;
      canvas.style.display = "block";
      await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
      if (cancelled) return;

      container.innerHTML = "";
      container.appendChild(canvas);

      // ── Fetch annotations ─────────────────────────────────────────────────
      const annotations = await page.getAnnotations({ intent: "display" });
      if (cancelled) return;

      // ── Pre-fill annotationStorage BEFORE rendering ───────────────────────
      // pdfjs reads from annotationStorage to set initial field values, so we
      // must populate it first. This also lets pdfjs create proper <textarea>
      // for multiLine fields and handle checkboxes/radios natively.
      for (const ann of annotations) {
        if (ann.subtype !== "Widget" || !ann.fieldName) continue;
        const val = report[ann.fieldName];
        if (val === undefined || val === null || val === "") continue;

        if (ann.checkBox) {
          pdf.annotationStorage.setValue(ann.id, {
            value: val === true || val === "true",
          });
        } else if (ann.radioButton) {
          // Radio: mark this button checked if its buttonValue matches stored value
          pdf.annotationStorage.setValue(ann.id, {
            value: String(val) === String(ann.buttonValue) ? ann.buttonValue : "Off",
          });
        } else {
          pdf.annotationStorage.setValue(ann.id, { value: String(val) });
        }
      }

      // ── Build annotation layer ────────────────────────────────────────────
      const annDiv = document.createElement("div");
      annDiv.className = "annotationLayer";
      container.appendChild(annDiv);
      annDivRef.current = annDiv;

      const annLayer = new AnnotationLayer({
        div:               annDiv,
        page,
        viewport:          vp.clone({ dontFlip: true }),
        annotationStorage: pdf.annotationStorage,
        accessibilityManager:      null,
        annotationCanvasMap:       null,
        annotationEditorUIManager: null,
      });

      await annLayer.render({
        annotations,
        downloadManager:    null,
        imageResourcesPath: "",
        renderForms:        true,
        enableScripting:    false,
        hasJSActions:       false,
        fieldObjects:       null,
      });
      if (cancelled) return;

      // Fix dimensions (setLayerDimensions uses CSS vars that may not resolve)
      annDiv.style.width  = width  + "px";
      annDiv.style.height = height + "px";

      // ── Capture changes via DOM events ────────────────────────────────────
      annDiv.addEventListener("change", (e) => {
        const { name, type, value, checked } = e.target;
        if (!name) return;
        onChange(name, type === "checkbox" ? checked : type === "radio" ? value : value);
      });
      annDiv.addEventListener("input", (e) => {
        const { tagName, type, name, value } = e.target;
        if (name && (tagName === "TEXTAREA" || (tagName === "INPUT" && type !== "checkbox" && type !== "radio"))) {
          onChange(name, value);
        }
      });
    })();

    return () => { cancelled = true; };
  }, [pageData, pdf]); // eslint-disable-line

  // Sync external report changes into existing DOM inputs
  useEffect(() => {
    const annDiv = annDivRef.current;
    if (!annDiv) return;
    for (const el of annDiv.querySelectorAll("input, select, textarea")) {
      if (!el.name || report[el.name] === undefined) continue;
      if (document.activeElement === el) continue;
      const val = report[el.name];
      if (el.type === "radio")     { el.checked = el.value === String(val); }
      else if (el.type === "checkbox") { el.checked = val === true || val === "true"; }
      else { el.value = val ?? ""; }
    }
  }, [report]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        flexShrink: 0,
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
        background: "#fff",
        "--total-scale-factor": pageData.scale,
        "--scale-round-x": "1px",
        "--scale-round-y": "1px",
      }}
    />
  );
}

const S = {
  placeholder: {
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "100%", color: "#64748b", fontSize: 14, fontStyle: "italic",
  },
};
